import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "vendor-message-detail-page-shell" }
const _hoisted_2 = {
  key: 0,
  class: "result-error"
}
const _hoisted_3 = {
  key: 0,
  class: "fetching-state"
}
const _hoisted_4 = {
  key: 1,
  class: "thread-content"
}
const _hoisted_5 = {
  key: 0,
  class: "avatar"
}
const _hoisted_6 = ["onClick", "innerHTML"]
const _hoisted_7 = { class: "bubble-meta" }
const _hoisted_8 = { class: "composer" }
const _hoisted_9 = { class: "composer-field" }

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppTextArea from "@/shared/components/ui/AppTextArea.vue";
import LoadingState from "@/shared/components/feedback/LoadingState.vue";
import {
  fetchVendorMessageDetail,
  sendVendorMessage,
} from "@/shared/services/messages.api";
import { useMessageHeaderStore } from "@/shared/stores/messageHeader";
import { presetConversations } from "./presetConversations";
import { timeAgo } from "@/shared/utils/utils";
import { getCurrentUserRole } from "@/shared/utils/currentUserRole";
import { getActiveContext } from "@/modules/auth/services/auth.api";
type MessageItem = {
  id: string;
  role: "incoming" | "outgoing";
  initials: string;
  message_type: string;
  metadata?: {
    table_id?: string;
    table_name?: string;
  };
  text: string;
  time: string;
};

type MessageDetailRecord = {
  chat_thread_id: number;
  chat_thread_type: string;
  title: string;
  status: string;
  client: {
    id: number;
    name: string;
    avatar: string;
    initials: string;
  };
  messages: Array<{
    id: number;
    body: string;
    message_type: string;
    metadata: {
      table_id?: string;
      table_name?: string;
    };
    is_system_message: boolean;
    created_at: string;
    sender: {
      role: string | null;
      name: string;
      avatar: string | null;
    };
  }>;
};

type MessageDetailResponse = MessageDetailRecord | { data: MessageDetailRecord };


export default /*@__PURE__*/_defineComponent({
  __name: 'VendorMessageDetailPage',
  setup(__props) {

const router = useRouter();
const route = useRoute();
const messageHeader = useMessageHeaderStore();
const messageInput = ref("");
const messages = ref<MessageItem[]>(presetConversations);
const isLoading = ref(false);
const isSending = ref(false);
const errorMessage = ref("");
const chatThreadRef = ref<HTMLElement | null>(null);
const currentThreadId = computed(() => asString(route.params.id).trim());
let activeVendorThreadId = "";
let activeVendorChannel: any = null;

function asString(value: unknown, fallback = "") {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return fallback;
}

function getAppointmentDetailRouteName() {
  const storedRole = String(getCurrentUserRole() || "").trim().toLowerCase();
  const activeContext = (getActiveContext() as any) || {};
  const contextRoles = Array.isArray(activeContext.roles)
    ? activeContext.roles.map((role: unknown) => String(role || "").trim().toLowerCase()).filter(Boolean)
    : [];
  const role =
    storedRole ||
    contextRoles.find((item) => item === "service_provider" || item === "provider") ||
    contextRoles[0] ||
    String(activeContext.type || "").trim().toLowerCase();
  if (role === "service_provider" || role === "provider" || role.includes("provider")) {
    return "provider.appointment.detail";
  }
  return "vendor.appointment.detail";
}

function getInitials(value: string) {
  const normalized = value.trim().replace(/\s+/g, " ");
  if (!normalized) return "NA";

  const parts = normalized.split(" ");
  const initials =
    parts.length >= 2
      ? `${parts[0].charAt(0)}${parts[1].charAt(0)}`
      : parts[0].slice(0, 2);

  return initials.toUpperCase().padEnd(2, "X").slice(0, 2);
}

function toHeaderVendor(client: MessageDetailRecord["client"]) {
  return {
    id: client.id,
    name: client.name,
    logo: client.avatar,
    initials: client.initials || getInitials(client.name),
  };
}

function normalizeMessage(item: MessageDetailRecord["messages"][number]): MessageItem {
  const senderName = item.sender.name;
  return {
    id: String(item.id),
    role: item.sender.role === "vendor" ? "outgoing" : "incoming",
    initials: getInitials(senderName),
    message_type: item.message_type,
    metadata: item.metadata,
    text: item.body,
    time: item.created_at,
  };
}

function normalizeReverbMessage(event: any): MessageItem | null {
  const payload = event?.message ?? event;
  if (
    !payload ||
    payload.id == null ||
    !payload.body ||
    !payload.created_at ||
    !payload.sender
  ) {
    return null;
  }

  return {
    id: String(payload.id),
    role: payload.sender.role === "vendor" ? "outgoing" : "incoming",
    initials: getInitials(payload.sender.name || "NA"),
    message_type: payload.message_type || "text",
    text: payload.body,
    time: payload.created_at,
  };
}

function appendReverbMessage(event: any) {
  const normalized = normalizeReverbMessage(event);
  if (!normalized) return;

  if (
    messages.value.some(
      (item) =>
        item.id === normalized.id ||
        (item.role === normalized.role &&
          item.text === normalized.text &&
          item.time === normalized.time)
    )
  ) {
    return;
  }

  messages.value = [...messages.value, normalized];
}

function getMessageThread(response: MessageDetailResponse) {
  if ("messages" in response && Array.isArray(response.messages)) {
    return response.messages;
  }

  if ("data" in response && response.data && Array.isArray(response.data.messages)) {
    return response.data.messages;
  }

  return [];
}

function listenToReverb(threadId: string) {
  if (!threadId) return;

  if (!window.Echo) {
    return;
  }

  if (activeVendorThreadId === threadId) {
    return;
  }

  if (activeVendorThreadId) {
    stopListeningToReverb(activeVendorThreadId);
  }

  console.log("Listening to Reverb channel:", `chat.thread.${threadId}`);

  activeVendorChannel = window.Echo.private(`chat.thread.${threadId}`);
  activeVendorChannel.listen(".message.sent", (event: any) => {
    console.log("Reverb message received:", event);
    appendReverbMessage(event);
  });
  activeVendorThreadId = threadId;
}

function stopListeningToReverb(threadId: string) {
  if (!threadId || !window.Echo) return;
  if (activeVendorThreadId !== threadId) return;

  console.log("Leaving Reverb channel:", `private-chat.thread.${threadId}`);

  if (activeVendorChannel?.stopListening) {
    activeVendorChannel.stopListening(".message.sent");
  }

  window.Echo.leave(`private-chat.thread.${threadId}`);
  activeVendorChannel = null;
  activeVendorThreadId = "";
}

function handleAuthChanged() {
  if (currentThreadId.value) {
    listenToReverb(currentThreadId.value);
  }
}

async function loadConversation() {
  const id = asString(route.params.id).trim();
  if (!id) {
    errorMessage.value = "Conversation not found.";
    messageHeader.reset();
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";
  messageHeader.setHeader({
    isLoading: true,
    title: "",
    chatThreadType: "",
    vendor: null,
  });

  try {
    const response = (await fetchVendorMessageDetail(id)) as MessageDetailResponse;
    if ("data" in response && response.data) {
      messageHeader.setHeader({
        title: response.data.title,
        chatThreadType: response.data.chat_thread_type,
        vendor: toHeaderVendor(response.data.client),
        isLoading: false,
      });
    } else if ("title" in response) {
      messageHeader.setHeader({
        title: response.title,
        chatThreadType: response.chat_thread_type,
        vendor: toHeaderVendor(response.client),
        isLoading: false,
      });
    } else {
      messageHeader.setHeader({ isLoading: false });
    }
    messages.value = getMessageThread(response).map(normalizeMessage);
  } catch (error) {
    console.error(error);
    errorMessage.value = "Unable to load conversation.";
    messageHeader.setHeader({ isLoading: false });
  } finally {
    isLoading.value = false;
  }
}

function scrollToBottom() {
  nextTick(() => {
    requestAnimationFrame(() => {
      const el = chatThreadRef.value;
      if (!el) return;

      if (typeof el.scrollTo === "function") {
        el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
        return;
      }

      el.scrollTop = el.scrollHeight;
    });
  });
}

async function handleSend() {
  if (isSending.value) return;
  const text = messageInput.value.trim();
  if (!text) return;
  const id = asString(route.params.id).trim();
  if (!id) return;

  isSending.value = true;
  try {
    const response = await sendVendorMessage(id, text);
    const thread = getMessageThread(response as MessageDetailResponse);
    if (thread.length > 0) {
      messages.value = thread.map(normalizeMessage);
    } else {
      const outgoing: MessageItem = {
        id: `local-${Date.now()}`,
        role: "outgoing",
        initials: "",
        text,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        message_type: "text",
      };
      // messages.value = [...messages.value, outgoing];
    }
    messageInput.value = "";
  } catch (error) {
    console.error("Failed to send message", error);
    errorMessage.value = "Failed to send message.";
  } finally {
    isSending.value = false;
  }
}

function handleBubbleClick(item: MessageItem) {
  if (item.metadata?.table_id) {
    console.log("Bubble clicked with table_id:", item.metadata);
    switch (item.metadata.table_name) {
      case "quote_enqs":
        router.push({
          name: "vendor.inquiry.detail",
          params: { id: item.metadata.table_id },
        });

        break;

      case "appointments":
        router.push({
          name: getAppointmentDetailRouteName(),
          params: { id: item.metadata.table_id },
        });

        break;
      case "quotes":
        router.push({
          name: "vendor.quotations.detail",
          params: { id: item.metadata.table_id },
        });

        break;
      default:
        console.log("Unknown table name");
    }
  }
}

watch(
  () => currentThreadId.value,
  async (newThreadId, oldThreadId) => {
    if (oldThreadId) {
      stopListeningToReverb(oldThreadId);
    }

    await loadConversation();

    if (newThreadId) {
      listenToReverb(newThreadId);
    }
  },
  { immediate: true }
);

watch(
  messages,
  () => {
    scrollToBottom();
  },
  { flush: "post" }
);

watch(
  () => isLoading.value,
  (loading, previousLoading) => {
    if (previousLoading && !loading) {
      scrollToBottom();
    }
  },
  { flush: "post" }
);

onMounted(() => {
  window.addEventListener("auth-changed", handleAuthChanged);
});

onUnmounted(() => {
  window.removeEventListener("auth-changed", handleAuthChanged);
  if (currentThreadId.value) {
    stopListeningToReverb(currentThreadId.value);
  }

  messageHeader.reset();
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (activeVendorThreadId) {
      stopListeningToReverb(activeVendorThreadId);
    }
  });
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(errorMessage.value), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      ref_key: "chatThreadRef",
      ref: chatThreadRef,
      class: "chat-thread"
    }, [
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(LoadingState, {
              loading: isLoading.value,
              message: "Loading conversation...",
              "spinner-size": 24,
              "spinner-width": 3
            }, null, 8, ["loading"])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(messages.value, (item) => {
              return (_openBlock(), _createElementBlock("div", {
                key: item.id,
                class: _normalizeClass(["message-row", item.role])
              }, [
                (item.role === 'incoming')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(item.initials), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("div", {
                  class: _normalizeClass(["bubble", item.role])
                }, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["bubble-text", item.message_type]),
                    onClick: ($event: any) => (handleBubbleClick(item)),
                    innerHTML: item.text
                  }, null, 10, _hoisted_6),
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("span", null, _toDisplayString(_unref(timeAgo)(item.time)), 1),
                    (item.role === 'outgoing')
                      ? (_openBlock(), _createBlock(_component_v_icon, {
                          key: 0,
                          size: "12"
                        }, {
                          default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                            _createTextVNode("mdi-check-all", -1)
                          ]))]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ])
                ], 2)
              ], 2))
            }), 128))
          ]))
    ], 512),
    _createElementVNode("div", _hoisted_8, [
      _createElementVNode("div", _hoisted_9, [
        _createVNode(AppTextArea, {
          "hide-details": "",
          density: "comfortable",
          rows: "2",
          modelValue: messageInput.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((messageInput).value = $event)),
          placeholder: "Type a message..."
        }, null, 8, ["modelValue"])
      ]),
      _createVNode(_component_v_btn, {
        icon: "",
        color: "primary",
        loading: isSending.value,
        disabled: !messageInput.value.trim() || isSending.value,
        onClick: handleSend
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_icon, { size: "18" }, {
            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createTextVNode("mdi-send", -1)
            ]))]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["loading", "disabled"])
    ])
  ]))
}
}

})